import { useMusicState } from '@hooks/storeHooks/useMusicState';
import { useTranslation } from 'react-i18next';
import { sendPrintMessage, sendStartOverMessage } from '@utils/station';
import { compositeImageWithQR } from '@utils/compositeImageWithQR';
import { getOrganizationId } from '@utils/getOrganization';
import {
  setHandleNextFunction,
  setHandleShareFunction,
  setSwitchToNextVariantFunction,
} from '@utils/station';
import QRCode from 'qrcode';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { DemoFan } from '../../../../../datas/demoFan';
import { OidcUser } from '../../../../../types/oidcUser';
import { StationCoverView } from '../StationCoverView/StationCoverView';
import { StationFinalView } from '../StationFinalView/StationFinalView';
import { StationGlowAnimation } from '../StationGlowAnimation/StationGlowAnimation';

interface StationDesignerUIProps {
  designImage1: string | null;
  designImage2: string | null;
  isLoadingDesignImages: boolean;
  userInput: string;
  fanData?: DemoFan;
  isRegeneratingImage: boolean;
  savedImageCreationId1: React.MutableRefObject<string | null>;
  savedImageCreationId2: React.MutableRefObject<string | null>;
  image1SavedId?: string | null;
  image2SavedId?: string | null;
  onRegenerateCover?: (imageVersion: 1 | 2) => void;
  onImageClick?: (imageVersion: 1 | 2) => void;
}

type StationDesignerState = 'cover' | 'final';

export const StationDesignerUI = ({
  designImage1,
  designImage2,
  isLoadingDesignImages,
  userInput,
  isRegeneratingImage,
  savedImageCreationId1,
  savedImageCreationId2,
  image1SavedId,
  image2SavedId,
  onImageClick,
}: StationDesignerUIProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const orgId = useMemo(() => getOrganizationId(user as OidcUser), [user]);
  const { volume, setVolume } = useMusicState();
  const [currentState, setCurrentState] = useState<StationDesignerState>('cover');
  const [currentImageVersion, setCurrentImageVersion] = useState<1 | 2>(1);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [isPrinting, setIsPrinting] = useState(false);

  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
  const swipeContainerRef = useRef<HTMLDivElement>(null);
  const qrCodeSavedIdRef = useRef<string | null | undefined>(null);

  const displayImage = currentImageVersion === 1 ? designImage1 : designImage2;
  const currentImageReady = !!displayImage;
  const hasMultipleVariations = !!designImage1 && !!designImage2;

  const currentSavedId =
    currentImageVersion === 1
      ? image1SavedId || savedImageCreationId1.current
      : image2SavedId || savedImageCreationId2.current;

  const isGeneratingOrRegenerating =
    isLoadingDesignImages || isRegeneratingImage;

  const USE_IMAGE_BACKGROUND = true;
  const showCoverGlassEffect = currentState === 'cover' && isRegeneratingImage;
  const isGeneratingImage =
    currentState === 'cover' && !currentImageReady && !isRegeneratingImage;

  useEffect(() => {
    if (
      currentState === 'final' &&
      currentImageReady &&
      currentSavedId &&
      qrCodeSavedIdRef.current !== currentSavedId
    ) {
      if (!orgId) return;
      const imageId = currentSavedId;
      if (imageId) {
        const baseUrl = window.location.origin;
        const url = `${baseUrl}/creations/image/${orgId}/${imageId}`;

        QRCode.toDataURL(url, {
          width: 300,
          margin: 2,
          errorCorrectionLevel: 'H',
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        })
          .then((dataUrl) => {
            setQrCodeDataUrl(dataUrl);
            qrCodeSavedIdRef.current = currentSavedId;
          })
          .catch((err) => {
            if (import.meta.env.DEV) {
              console.error('[QR Code] Failed to generate QR code:', err);
            }
          });
      }
    }
  }, [
    currentState,
    currentImageReady,
    orgId,
    currentSavedId,
    currentImageVersion,
  ]);

  const handleNext = useCallback(() => {
    if (currentState === 'cover') {
      if (!currentImageReady) return;
      setCurrentState('final');
    }
  }, [currentState, currentImageReady]);

  const handleShare = useCallback(() => {
    if (currentState === 'cover' && currentSavedId && currentImageReady) {
      setCurrentState('final');
    }
  }, [currentState, currentSavedId, currentImageReady]);

  const handleSwitchToNextVariant = useCallback(() => {
    if (!hasMultipleVariations) return;
    const nextVersion: 1 | 2 = currentImageVersion === 1 ? 2 : 1;
    const nextImage = nextVersion === 1 ? designImage1 : designImage2;
    if (!nextImage) return;
    setCurrentImageVersion(nextVersion);
    onImageClick?.(nextVersion);
  }, [
    hasMultipleVariations,
    currentImageVersion,
    designImage1,
    designImage2,
    onImageClick,
  ]);

  useEffect(() => {
    setHandleNextFunction(handleNext);
    setHandleShareFunction(handleShare);
    setSwitchToNextVariantFunction(handleSwitchToNextVariant);

    return () => {
      setHandleNextFunction(null);
      setHandleShareFunction(null);
      setSwitchToNextVariantFunction(null);
    };
  }, [handleNext, handleShare, handleSwitchToNextVariant]);

  const handleStartOver = useCallback(() => {
    sendStartOverMessage();
    navigate('/');
  }, [navigate]);

  const handlePrint = useCallback(async () => {
    if (!displayImage || isPrinting) return;

    setIsPrinting(true);
    try {
      if (qrCodeDataUrl) {
        const compositedImage = await compositeImageWithQR(
          displayImage,
          qrCodeDataUrl,
        );
        sendPrintMessage(compositedImage);
      } else {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            setIsPrinting(false);
            return;
          }
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          const base64 = canvas.toDataURL('image/png');
          sendPrintMessage(base64);
          setIsPrinting(false);
        };
        img.onerror = () => {
          console.error('Failed to load image for printing');
          setIsPrinting(false);
        };
        img.src = displayImage;
        return;
      }
      setIsPrinting(false);
    } catch (error) {
      console.error('Error preparing image for print:', error);
      setIsPrinting(false);
    }
  }, [displayImage, qrCodeDataUrl, isPrinting]);

  const handleSwipeStart = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      swipeStartRef.current = { x: clientX, y: clientY };
    },
    [],
  );

  const handleSwipeMove = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      if (!swipeStartRef.current) return;
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
      const deltaX = Math.abs(clientX - swipeStartRef.current.x);
      const deltaY = Math.abs(clientY - swipeStartRef.current.y);
      // Only prevent default if horizontal movement is greater (only for mouse events)
      if (deltaX > deltaY && deltaX > 10 && !('touches' in e)) {
        e.preventDefault();
      }
    },
    [],
  );

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!swipeStartRef.current) return;
    const clientX = e.touches[0].clientX;
    const clientY = e.touches[0].clientY;
    const deltaX = Math.abs(clientX - swipeStartRef.current.x);
    const deltaY = Math.abs(clientY - swipeStartRef.current.y);
    if (deltaX > deltaY && deltaX > 10) {
      e.preventDefault();
    }
  }, []);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const clientX = e.touches[0].clientX;
    const clientY = e.touches[0].clientY;
    swipeStartRef.current = { x: clientX, y: clientY };
  }, []);

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!swipeStartRef.current || !hasMultipleVariations) return;

      const clientX = e.changedTouches[0].clientX;
      const clientY = e.changedTouches[0].clientY;

      const deltaX = clientX - swipeStartRef.current.x;
      const deltaY = clientY - swipeStartRef.current.y;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      if (absDeltaX > absDeltaY && absDeltaX > 50) {
        if (deltaX > 0 && currentImageVersion === 2) {
          setCurrentImageVersion(1);
          onImageClick?.(1);
        } else if (deltaX < 0 && currentImageVersion === 1) {
          setCurrentImageVersion(2);
          onImageClick?.(2);
        }
      }

      swipeStartRef.current = null;
    },
    [hasMultipleVariations, currentImageVersion, onImageClick],
  );

  useEffect(() => {
    const container = swipeContainerRef.current;
    if (!container) return;

    container.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    container.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  const handleSwipeEnd = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      if (!swipeStartRef.current || !hasMultipleVariations) return;

      const clientX =
        'changedTouches' in e ? e.changedTouches[0].clientX : e.clientX;
      const clientY =
        'changedTouches' in e ? e.changedTouches[0].clientY : e.clientY;

      const deltaX = clientX - swipeStartRef.current.x;
      const deltaY = clientY - swipeStartRef.current.y;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      if (absDeltaX > absDeltaY && absDeltaX > 50) {
        if (deltaX > 0 && currentImageVersion === 2) {
          setCurrentImageVersion(1);
          onImageClick?.(1);
        } else if (deltaX < 0 && currentImageVersion === 1) {
          setCurrentImageVersion(2);
          onImageClick?.(2);
        }
      }

      swipeStartRef.current = null;
    },
    [hasMultipleVariations, currentImageVersion, onImageClick],
  );

  return (
    <div
      className="fixed inset-0 flex flex-col text-white overflow-hidden"
      style={{
        background: USE_IMAGE_BACKGROUND
          ? `url('/assets/images/ready.png') center center / cover no-repeat`
          : isGeneratingOrRegenerating
          ? 'linear-gradient(180deg, #7136BE 0%, #7136BE 30%, #D400A1 60%, #D400A1 80%, #000 100%)'
          : 'linear-gradient(180deg, #7136BE 0%, #7136BE 30%, #7136BE 60%, #7136BE 80%, #000 100%)',
      }}>
      {!USE_IMAGE_BACKGROUND && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: isGeneratingOrRegenerating
              ? `radial-gradient(900px 900px at 12% 50%, rgba(212,0,161,0.35), transparent 60%),
                 radial-gradient(900px 900px at 88% 50%, rgba(212,0,161,0.35), transparent 60%),
                 radial-gradient(900px 650px at 50% 10%, rgba(212,0,161,0.25), transparent 60%),
                 radial-gradient(900px 700px at 50% 95%, rgba(25,54,104,0.35), transparent 65%)`
              : `radial-gradient(900px 900px at 12% 50%, rgba(113,54,190,0.35), transparent 60%),
                 radial-gradient(900px 900px at 88% 50%, rgba(113,54,190,0.35), transparent 60%),
                 radial-gradient(900px 650px at 50% 10%, rgba(113,54,190,0.25), transparent 60%),
                 radial-gradient(900px 700px at 50% 95%, rgba(25,54,104,0.35), transparent 65%)`,
            filter: 'blur(80px)',
            animation: 'bgFloat 14s ease-in-out infinite',
          }}
        />
      )}
      {!USE_IMAGE_BACKGROUND && (
        <div
          className="absolute inset-0 pointer-events-none z-10"
          style={{
            background:
              'linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0.00) 100%)',
            backdropFilter: 'blur(0px)',
          }}
        />
      )}
      {!USE_IMAGE_BACKGROUND && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 30%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.1) 70%, transparent 100%)',
          }}
        />
      )}
      <StationGlowAnimation
        isVisible={USE_IMAGE_BACKGROUND && isGeneratingOrRegenerating}
      />
      <div className="relative z-20 flex flex-col h-full w-full p-10 sm:p-12 md:p-14">
        {currentState === 'cover' && (
          <div className="absolute top-11 right-8 sm:right-12 md:right-16 z-20 flex justify-end items-center">
            <button
              onClick={handleNext}
              disabled={!currentImageReady}
              className="px-6 py-4 bg-fuchsia-700 rounded-full text-white text-xl font-medium font-['DM_Sans'] leading-6 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-fuchsia-800 transition-colors shadow-[inset_0px_-3px_4.5px_0px_rgba(0,0,0,0.15)] shadow-[inset_0px_3px_4.5px_0px_rgba(255,150,217,0.50)]">
              {currentImageReady ? t('generation.share') : t('generation.next')}
            </button>
          </div>
        )}
        {currentState === 'final' && (
          <div className="absolute top-11 right-8 sm:right-12 md:right-16 z-20 flex justify-end items-center gap-3">
            {displayImage && (
              <button
                onClick={handlePrint}
                disabled={isPrinting}
                className="px-6 py-4 bg-fuchsia-700 rounded-full text-white text-xl font-medium font-['DM_Sans'] leading-6 hover:bg-fuchsia-800 transition-colors shadow-[inset_0px_-3px_4.5px_0px_rgba(0,0,0,0.15)] shadow-[inset_0px_3px_4.5px_0px_rgba(255,150,217,0.50)] disabled:opacity-50 disabled:cursor-not-allowed">
                {isPrinting ? t('generation.printing') : t('generation.print')}
              </button>
            )}
            <button
              onClick={handleStartOver}
              className="px-6 py-4 bg-fuchsia-700 rounded-full text-white text-xl font-medium font-['DM_Sans'] leading-6 hover:bg-fuchsia-800 transition-colors shadow-[inset_0px_-3px_4.5px_0px_rgba(0,0,0,0.15)] shadow-[inset_0px_3px_4.5px_0px_rgba(255,150,217,0.50)]">
              {t('generation.startOver')}
            </button>
          </div>
        )}
        <div className="relative flex flex-col w-full mt-[120px] h-[calc(100%-120px)]">
          {isGeneratingOrRegenerating && !USE_IMAGE_BACKGROUND && (
            <>
              <div
                className="absolute inset-0 w-full h-full pointer-events-none rounded-[36px]"
                style={{
                  border: '20px solid rgba(212, 0, 161, 0.8)',
                  boxShadow:
                    '0 0 80px rgba(212, 0, 161, 0.6), inset 0 0 80px rgba(212, 0, 161, 0.3)',
                  animation: 'borderGlow 2.4s ease-in-out infinite',
                }}
              />
              <div
                className="absolute pointer-events-none rounded-[36px]"
                style={{
                  left: '2.6%',
                  top: '0.9%',
                  width: '94.8%',
                  height: '98.2%',
                  border: '30px solid rgba(212, 0, 161, 0.3)',
                  boxShadow: '0 0 120px rgba(212, 0, 161, 0.5)',
                  filter: 'blur(40px)',
                  animation: 'borderGlow 2.4s ease-in-out infinite',
                }}
              />
            </>
          )}
          {(currentState === 'cover' || currentState === 'final') && (
            <div
              ref={swipeContainerRef}
              className="flex-1 w-full overflow-x-hidden overflow-y-auto select-none"
              onMouseDown={handleSwipeStart}
              onMouseMove={handleSwipeMove}
              onMouseUp={handleSwipeEnd}
              onMouseLeave={handleSwipeEnd}>
              {currentState === 'cover' && (
                <StationCoverView
                  title={userInput}
                  coverUrl={displayImage || undefined}
                  isRegeneratingImage={isRegeneratingImage}
                  showGlassEffect={showCoverGlassEffect}
                  isGeneratingImage={isGeneratingImage}
                  isPlaying={false}
                  duration={0}
                  currentTime={0}
                  displayDuration="00:00"
                  waveformData={[]}
                  progress={0}
                  volume={volume}
                  onVolumeChange={setVolume}
                  onPlayPause={() => {}}
                  onSeek={() => {}}
                  onCardClick={() => {
                    onImageClick?.(currentImageVersion);
                  }}
                  hasMultipleVariations={hasMultipleVariations}
                  hideAudioControls={true}
                />
              )}
              {currentState === 'final' && (
                <StationFinalView
                  title={userInput}
                  coverUrl={displayImage || undefined}
                  qrCodeDataUrl={qrCodeDataUrl}
                  isPlaying={false}
                  duration={0}
                  currentTime={0}
                  displayDuration="00:00"
                  waveformData={[]}
                  progress={0}
                  volume={volume}
                  onVolumeChange={setVolume}
                  onPlayPause={() => {}}
                  onSeek={() => {}}
                  hasMultipleVariations={hasMultipleVariations}
                  hideAudioControls={true}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
